import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Dropdown, Form, Row, Spinner, Table } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../components/common/Loader';
import EmptyState from '../components/common/EmptyState';
import TagInput from '../components/common/TagInput';
import MedicineRow, { EMPTY_MEDICINE } from '../components/doctor/MedicineRow';
import TestsAdvised from '../components/doctor/TestsAdvised';
import PaymentModal from '../components/patient/PaymentModal';
import { consultationApi, masterApi, patientApi, visitApi } from '../api';
import { useToast } from '../context/ToastContext';
import {
  durationInDays,
  fileUrl,
  formatAge,
  formatDate,
  formatMoney,
  formatTime,
  timeAgo,
  titleCase,
  toInputDate,
} from '../utils/format';

const COMMON_DIAGNOSES = [
  'Viral Fever', 'Upper Respiratory Tract Infection', 'Acute Gastritis', 'Gastroenteritis',
  'Hypertension', 'Type 2 Diabetes Mellitus', 'Anaemia', 'Migraine', 'Allergic Rhinitis',
  'Urinary Tract Infection', 'Bronchial Asthma', 'Dengue Fever', 'Typhoid',
];

const COMMON_TESTS = ['CBC', 'Blood Sugar (F/PP)', 'HbA1c', 'Urine Routine', 'LFT', 'KFT', 'Lipid Profile', 'Thyroid Profile', 'Chest X-Ray', 'ECG', 'USG Abdomen'];

const EMPTY_FORM = {
  complaints: [],
  examination: '',
  diagnosis: [],
  medicines: [{ ...EMPTY_MEDICINE }],
  precautions: [],
  testsAdvised: [],
  followUpDate: '',
};

export default function Consultation() {
  const { visitId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [visit, setVisit] = useState(null);
  const [history, setHistory] = useState([]);
  const [medicineMaster, setMedicineMaster] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [referralClinics, setReferralClinics] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Once the doctor picks a date by hand the medicines stop moving it.
  const [followUpPicked, setFollowUpPicked] = useState(false);
  const [collecting, setCollecting] = useState(false);

  const load = useCallback(async () => {
    try {
      const visitRes = await visitApi.get(visitId);
      setVisit(visitRes.data);

      const [histRes, medRes, tplRes, refRes] = await Promise.all([
        patientApi.history(visitRes.data.patient._id),
        masterApi.medicines({ limit: 500 }),
        masterApi.precautions(),
        masterApi.referrals(),
      ]);

      setHistory(histRes.data.timeline.filter((v) => v._id !== visitId && v.consultation));
      setMedicineMaster(medRes.data);
      setTemplates(tplRes.data);
      setReferralClinics(refRes.data);

      const c = visitRes.data.consultation;
      if (c) {
        setForm({
          complaints: c.complaints || [],
          examination: c.examination || '',
          diagnosis: c.diagnosis || [],
          medicines: c.medicines?.length ? c.medicines : [{ ...EMPTY_MEDICINE }],
          precautions: c.precautions || [],
          // Each test keeps its own clinic; it arrives as an id, or as the
          // whole clinic when the server populated it.
          testsAdvised: (c.testsAdvised || []).map((t) => ({
            name: t.name || '',
            referralClinic: t.referralClinic?._id || t.referralClinic || '',
          })),
          followUpDate: toInputDate(c.followUpDate),
        });
        // A date already saved is the doctor's decision - leave it alone.
        if (c.followUpDate) setFollowUpPicked(true);
      } else if (visitRes.data.reasonForVisit) {
        // Seed the complaint list with what reception typed in.
        setForm({ ...EMPTY_FORM, complaints: [{ text: visitRes.data.reasonForVisit, duration: '' }] });
      }
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  }, [visitId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Pulls just the money side of the visit back in after a payment. A full
   * reload would rebuild the form from what is saved and throw away whatever
   * the doctor has typed but not saved yet.
   */
  const refreshFees = useCallback(async () => {
    try {
      const res = await visitApi.get(visitId);
      setVisit((v) => (v ? { ...v, fees: res.data.fees } : res.data));
    } catch {
      // The bill catches up the next time the screen is opened.
    }
  }, [visitId]);

  // Warn before losing typed-but-unsaved work.
  useEffect(() => {
    function beforeUnload(e) {
      if (!dirty) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    }
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty]);

  const lastVisit = history[0];

  const complaintTexts = useMemo(() => form.complaints.map((c) => c.text), [form.complaints]);

  // Vitals reception recorded, with the out-of-range ones flagged for the doctor.
  const vitalPills = useMemo(() => {
    const v = visit?.vitals || {};
    return [
      v.weight && { label: 'Weight', value: `${v.weight} kg` },
      v.bpSystolic && {
        label: 'BP',
        value: `${v.bpSystolic}/${v.bpDiastolic}`,
        high: v.bpSystolic >= 140 || v.bpDiastolic >= 90,
      },
      v.pulse && { label: 'Pulse', value: v.pulse, high: v.pulse > 100 || v.pulse < 55 },
      v.temperature && { label: 'Temp', value: `${v.temperature} F`, high: v.temperature >= 100 },
      v.spo2 && { label: 'SpO2', value: `${v.spo2}%`, high: v.spo2 < 95 },
      v.sugar && { label: 'Sugar', value: v.sugar, high: v.sugar >= 200 },
    ].filter(Boolean);
  }, [visit]);

  // The patient comes back when the longest course runs out.
  const longestCourse = useMemo(
    () =>
      form.medicines
        .filter((m) => m.name.trim())
        .reduce((most, m) => Math.max(most, durationInDays(m.duration)), 0),
    [form.medicines],
  );

  useEffect(() => {
    if (followUpPicked || !longestCourse) return;

    const due = visit?.visitDate ? new Date(visit.visitDate) : new Date();
    due.setDate(due.getDate() + longestCourse);
    const next = toInputDate(due);

    setForm((f) => (f.followUpDate === next ? f : { ...f, followUpDate: next }));
  }, [longestCourse, followUpPicked, visit]);

  const medicineCount = form.medicines.filter((m) => m.name.trim()).length;

  // What the medicines add up to. Only the ones with a price on the master
  // count towards it - the rest are simply not charged here.
  const medicineTotal = form.medicines.reduce(
    (sum, m) => sum + (m.price || 0) * (m.quantity || 0),
    0,
  );

  /**
   * Today's bill. The medicines figure is the live one from the table rather
   * than what is stored, so the doctor sees the amount move as the
   * prescription is written; it is saved when the visit is finished.
   */
  const bill = useMemo(() => {
    const fees = visit?.fees || {};
    const consultation = fees.consultation || 0;
    const discount = fees.discount || 0;
    const medicines = Math.round(medicineTotal);
    const total = Math.max(0, consultation + medicines - discount);
    const paid = fees.paid || 0;

    return { consultation, medicines, discount, total, paid, balance: Math.max(0, total - paid) };
  }, [visit, medicineTotal]);
  const attachments = visit?.patient?.reports || [];

  /** Blocks the back / queue buttons while there is typed-but-unsaved work. */
  function leave(to) {
    if (dirty && !window.confirm('You have unsaved changes. Leave this consultation anyway?')) return;
    navigate(to);
  }

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    setDirty(true);
  }

  function setMedicine(index, next) {
    setForm((f) => ({ ...f, medicines: f.medicines.map((m, i) => (i === index ? next : m)) }));
    setDirty(true);
  }

  function addMedicine() {
    setForm((f) => ({ ...f, medicines: [...f.medicines, { ...EMPTY_MEDICINE }] }));
    setDirty(true);
  }

  function removeMedicine(index) {
    setForm((f) => {
      const list = f.medicines.filter((_, i) => i !== index);
      return { ...f, medicines: list.length ? list : [{ ...EMPTY_MEDICINE }] };
    });
    setDirty(true);
  }

  function repeatLast() {
    if (!lastVisit?.consultation) return;
    const c = lastVisit.consultation;
    setForm((f) => ({
      ...f,
      medicines: c.medicines?.length ? c.medicines.map((m) => ({ ...m })) : f.medicines,
      diagnosis: f.diagnosis.length ? f.diagnosis : c.diagnosis || [],
      precautions: f.precautions.length ? f.precautions : c.precautions || [],
    }));
    setDirty(true);
    toast.info(`Copied the prescription from ${formatDate(lastVisit.visitDate)}`);
  }

  /**
   * The dialog collects against what is saved on the visit, so anything typed
   * but not saved is put away first - otherwise it would ask for the
   * consultation fee alone and miss the medicines.
   */
  async function openPayment() {
    if (dirty) await save(false, true);
    setCollecting(true);
  }

  async function applyTemplate(tpl) {
    try {
      const res = await consultationApi.applyTemplates([tpl._id]);
      const merged = [...form.precautions];
      res.data.forEach((line) => {
        if (!merged.includes(line)) merged.push(line);
      });
      set('precautions', merged);
      toast.success(`Added "${tpl.title}" advice`);
    } catch (err) {
      toast.error(err);
    }
  }

  /**
   * @param finalise  true marks the visit done, drops it off the waiting list
   *                  and goes on to the printable prescription
   * @param quiet     saves without a toast, for the payment button
   */
  async function save(finalise, quiet = false) {
    const medicines = form.medicines.filter((m) => m.name.trim());

    if (finalise && !form.diagnosis.length && !medicines.length) {
      toast.warn('Add at least a diagnosis or one medicine before finishing');
      return;
    }

    setSaving(true);
    try {
      await consultationApi.save({
        visit: visitId,
        // The patient is messaged from the print screen instead, so the
        // message leaves with the printed sheet.
        complaints: form.complaints.filter((c) => c.text?.trim()),
        examination: form.examination,
        diagnosis: form.diagnosis,
        medicines,
        precautions: form.precautions,
        testsAdvised: form.testsAdvised,
        followUpDate: form.followUpDate || undefined,
        isFinalised: finalise,
      });

      setDirty(false);
      if (!finalise) {
        if (!quiet) toast.success('Draft saved');
        // A quiet save is on the way to the payment dialog - rebuilding the
        // form from the server here would be pointless work, and the bill is
        // refreshed on its own.
        if (quiet) await refreshFees();
        else load();
      } else {
        toast.success('Prescription saved. Patient marked as done.');
        navigate(`/print/${visitId}`);
      }
    } catch (err) {
      toast.error(err);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Loader label="Opening the patient file..." className="py-5" />;
  if (!visit) return <Alert variant="danger">Visit not found.</Alert>;

  const p = visit.patient;

  return (
    <>
      {/* Patient strip - always visible while the doctor works */}
      <Card className="mb-4">
        <Card.Body className="py-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div className="d-flex align-items-center gap-3">
              <div className="token-badge">{visit.tokenNo}</div>
              <div>
                <div className="fw-bold fs-5">
                  {p.name}
                  <Badge bg={visit.visitType === 'new' ? 'success' : 'info'} className="ms-2" style={{ fontSize: '0.65rem' }}>
                    {visit.visitType === 'new' ? 'FIRST VISIT' : 'REVISIT'}
                  </Badge>
                </div>
                <div className="text-muted small">
                  {titleCase(p.gender)}, {formatAge(p)} &middot;{' '}
                  <i className="bi bi-telephone" /> {p.mobile} &middot; {formatTime(visit.visitDate)}
                </div>
              </div>
            </div>

            <div className="d-flex gap-2">
              <Button variant="outline-secondary" size="sm" onClick={() => leave(`/patients/${p._id}`)}>
                <i className="bi bi-person-lines-fill me-1" /> Full Record
              </Button>
              <Button variant="light" size="sm" onClick={() => leave('/dashboard')}>
                <i className="bi bi-arrow-left me-1" /> Back to Queue
              </Button>
            </div>
          </div>

          {vitalPills.length > 0 && (
            <div className="vitals-strip mt-3 pt-3 border-top">
              {vitalPills.map((pill) => (
                <div className={`vital-pill ${pill.high ? 'is-high' : ''}`} key={pill.label}>
                  <span className="vital-label">{pill.label}</span>
                  <strong>{pill.value}</strong>
                </div>
              ))}
            </div>
          )}

          {p.allergies?.length > 0 && (
            <div className="allergy-alert mt-3">
              <i className="bi bi-exclamation-triangle-fill me-2" />
              <strong>ALLERGIC TO: {p.allergies.join(', ')}</strong>
            </div>
          )}

          {p.chronicConditions?.length > 0 && (
            <div className="mt-2 small">
              <span className="text-muted me-2">Known conditions:</span>
              {p.chronicConditions.map((c) => (
                <Badge bg="warning" text="dark" key={c} className="me-1">
                  {c}
                </Badge>
              ))}
            </div>
          )}

          {/* Today's bill, kept in view while the prescription is written. The
              medicines figure follows the table live; it is written to the
              visit when the prescription is finished. */}
          <div className="bill-strip mt-3 pt-3 border-top">
            <div className="bill-line">
              <span>Consultation</span>
              <span>{formatMoney(bill.consultation)}</span>
            </div>
            <div className="bill-line">
              <span>Medicines</span>
              <span>{formatMoney(bill.medicines)}</span>
            </div>
            {bill.discount > 0 && (
              <div className="bill-line">
                <span>Discount</span>
                <span>- {formatMoney(bill.discount)}</span>
              </div>
            )}
            <div className="bill-line is-total">
              <span>Total</span>
              <span>{formatMoney(bill.total)}</span>
            </div>
            <div className="bill-line">
              <span>Received</span>
              <span className="text-success">{formatMoney(bill.paid)}</span>
            </div>
            <div className={`bill-line is-balance ${bill.balance > 0 ? 'is-due' : 'is-clear'}`}>
              <span>{bill.balance > 0 ? 'Still to pay' : 'Fully paid'}</span>
              <span>{formatMoney(bill.balance)}</span>
            </div>

            <Button
              size="sm"
              variant={bill.balance > 0 ? 'outline-success' : 'outline-secondary'}
              className="bill-collect"
              disabled={saving}
              onClick={openPayment}
            >
              {saving ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <i className="bi bi-cash-coin me-1" />
              )}
              {bill.balance > 0 ? 'Take payment' : 'Payments'}
            </Button>
          </div>
        </Card.Body>
      </Card>

      <PaymentModal
        show={collecting}
        patientId={p._id}
        onHide={() => setCollecting(false)}
        onChanged={refreshFees}
      />

      {/* The record so far, read before anything is typed - so it sits on top. */}
      <Row className="g-4 mb-4">
        <Col lg={attachments.length > 0 ? 6 : 12}>
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-clock-history me-2" />
                Past Visits ({history.length})
              </span>
              {lastVisit && (
                <Button size="sm" variant="outline-primary" onClick={repeatLast} title="Copy the last prescription into today's">
                  <i className="bi bi-arrow-repeat me-1" /> Repeat
                </Button>
              )}
            </Card.Header>
            <Card.Body className="consult-history">
              {history.length === 0 ? (
                <EmptyState icon="bi-file-earmark" title="First time here" hint="No previous visits on record." />
              ) : (
                history.map((h) => (
                  <div className="history-entry" key={h._id}>
                    <div className="fw-semibold small">
                      {formatDate(h.visitDate)}
                      <span className="text-muted fw-normal ms-2">{timeAgo(h.visitDate)}</span>
                    </div>
                    {h.consultation.diagnosis?.length > 0 && (
                      <div className="small" style={{ color: 'var(--clinic-primary)' }}>
                        {h.consultation.diagnosis.join(', ')}
                      </div>
                    )}
                    {h.consultation.complaints?.length > 0 && (
                      <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                        {h.consultation.complaints.map((c) => c.text).join(', ')}
                      </div>
                    )}
                    {h.consultation.medicines?.length > 0 && (
                      <ul className="mb-1 mt-1 ps-3" style={{ fontSize: '0.78rem' }}>
                        {h.consultation.medicines.map((m, i) => (
                          <li key={i}>
                            {m.name} &mdash; {m.frequency} &times; {m.duration}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0 text-decoration-none"
                        style={{ fontSize: '0.78rem' }}
                        onClick={() => {
                          setForm((f) => ({ ...f, medicines: h.consultation.medicines.map((m) => ({ ...m })) }));
                          setDirty(true);
                          toast.info('Medicines copied into today\'s prescription');
                        }}
                      >
                        <i className="bi bi-clipboard-plus me-1" />
                        Use these medicines
                      </Button>
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0 text-decoration-none text-muted"
                        style={{ fontSize: '0.78rem' }}
                        onClick={() => navigate(`/print/${h._id}`)}
                      >
                        <i className="bi bi-printer me-1" />
                        Print
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </Card.Body>
          </Card>
        </Col>

        {attachments.length > 0 && (
          <Col lg={6}>
            <Card className="h-100">
              <Card.Header>
                <i className="bi bi-paperclip me-2" />
                Reports ({attachments.length})
              </Card.Header>
              <Card.Body className="consult-history">
                <div className="attach-list">
                  {attachments.map((r) => (
                    <a
                      className="attach-item text-decoration-none"
                      key={r._id}
                      href={fileUrl(r.url)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <i
                        className={`bi ${r.type === 'pdf' ? 'bi-file-earmark-pdf' : 'bi-file-earmark-image'} attach-icon`}
                      />
                      <div className="flex-grow-1 min-w-0">
                        <span className="attach-name">{r.name}</span>
                        <div className="text-muted" style={{ fontSize: '0.74rem' }}>
                          {formatDate(r.uploadedAt)}
                          {r.note ? ` · ${r.note}` : ''}
                        </div>
                      </div>
                      <i className="bi bi-box-arrow-up-right text-muted flex-shrink-0" />
                    </a>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {/* Today's consultation, full width underneath. */}
    <Card className="mb-4">
      <Card.Header>
        <i className="bi bi-journal-medical me-2" />
        Step 1 &mdash; શુ થાય છે?
      </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Label>Complaints</Form.Label>
                <TagInput
                  value={complaintTexts}
                  onChange={(list) =>
                    set(
                      'complaints',
                      list.map((text) => form.complaints.find((c) => c.text === text) || { text, duration: '' }),
                    )
                  }
                  placeholder="e.g. Fever - press Enter"
                  suggestions={['Fever', 'Cough', 'Cold', 'Headache', 'Body ache', 'Vomiting', 'Loose motions', 'Stomach pain', 'Weakness', 'Chest pain', 'Breathlessness']}
                />
                {form.complaints.length > 0 && (
                  <div className="mt-2">
                    {form.complaints.map((c, i) => (
                      <div className="d-flex align-items-center gap-2 mb-1" key={i}>
                        <span className="small text-muted" style={{ minWidth: 110 }}>
                          {c.text}
                        </span>
                        <Form.Control
                          size="sm"
                          value={c.duration}
                          placeholder="since... e.g. 3 days"
                          onChange={(e) =>
                            set(
                              'complaints',
                              form.complaints.map((x, j) => (j === i ? { ...x, duration: e.target.value } : x)),
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}
              </Col>

              <Col md={6}>
                <Form.Label>Diagnosis</Form.Label>
                <TagInput
                  value={form.diagnosis}
                  onChange={(v2) => set('diagnosis', v2)}
                  placeholder="e.g. Viral Fever - press Enter"
                  suggestions={COMMON_DIAGNOSES}
                />

                <Form.Label className="mt-3">On Examination</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.examination}
                  onChange={(e) => set('examination', e.target.value)}
                  placeholder="Throat congested, chest clear, no oedema..."
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <span>
              <i className="bi bi-capsule me-2" />
              Step 2 &mdash; Medicines
              {medicineCount > 0 && <span className="text-muted fw-normal ms-2">({medicineCount})</span>}
            </span>
            <Button size="sm" className="btn-clinic" onClick={addMedicine}>
              <i className="bi bi-plus-lg me-1" /> Add Medicine
            </Button>
          </Card.Header>
          <div className="table-responsive">
            <Table className="mb-0 med-table align-middle" size="sm">
              <thead>
                <tr>
                  <th style={{ width: 30 }}>#</th>
                  <th>Medicine</th>
                  <th>Form</th>
                  <th>Freq</th>
                  <th>When</th>
                  <th>Duration</th>
                  <th>Qty</th>
                  <th>Note</th>
                  <th className="text-end">Amount</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {form.medicines.map((m, i) => (
                  <MedicineRow
                    key={i}
                    index={i}
                    value={m}
                    medicines={medicineMaster}
                    onChange={setMedicine}
                    onRemove={removeMedicine}
                    isLast={i === form.medicines.length - 1}
                    onAddRow={addMedicine}
                  />
                ))}
              </tbody>

              {medicineTotal > 0 && (
                <tfoot>
                  <tr className="med-total-row">
                    <td colSpan={8} className="text-end fw-semibold">
                      Total for the medicines
                    </td>
                    <td className="text-end fw-bold">{formatMoney(medicineTotal)}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </Table>
          </div>
          <Card.Footer className="bg-white text-muted small">
            <i className="bi bi-lightbulb me-1" />
            Frequency reads morning-noon-night. 1-0-1 means one in the morning and one at night. Press Enter on the
            last row to add another medicine.
          </Card.Footer>
        </Card>

        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <span>
              <i className="bi bi-shield-check me-2" />
              Step 3 &mdash; Advice &amp; Follow-up
            </span>
            {/* Always here, even before any template has been written - the
                menu then says where to go and make one. */}
            <Dropdown>
              <Dropdown.Toggle size="sm" variant="outline-secondary">
                <i className="bi bi-file-text me-1" /> Use a template
              </Dropdown.Toggle>
              <Dropdown.Menu align="end" style={{ maxHeight: 320, overflowY: 'auto' }}>
                {templates.length > 0 ? (
                  templates.map((t) => (
                    <Dropdown.Item key={t._id} onClick={() => applyTemplate(t)}>
                      {t.title}
                      <span className="text-muted small ms-2">({t.items.length} lines)</span>
                    </Dropdown.Item>
                  ))
                ) : (
                  <>
                    <Dropdown.Header>No advice templates yet</Dropdown.Header>
                    <Dropdown.Item onClick={() => leave('/masters')}>
                      <i className="bi bi-plus-lg me-2" />
                      Add one under Medicines &amp; Advice
                    </Dropdown.Item>
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={7}>
                <Form.Label>Precautions for the patient (printed on the prescription)</Form.Label>
                <TagInput
                  value={form.precautions}
                  onChange={(v2) => set('precautions', v2)}
                  placeholder="Type an instruction and press Enter"
                />
              </Col>

              <Col md={5}>
                <Form.Label>Follow-up date</Form.Label>
                <Form.Control
                  type="date"
                  value={form.followUpDate}
                  min={toInputDate(new Date())}
                  onChange={(e) => {
                    set('followUpDate', e.target.value);
                    // Clearing it hands the date back to the medicines.
                    setFollowUpPicked(!!e.target.value);
                  }}
                />
                <Form.Text className="text-muted">
                  {followUpPicked
                    ? 'Set by hand. Clear the box to follow the medicines again.'
                    : longestCourse
                      ? `Set from the longest course - ${longestCourse} day${longestCourse === 1 ? '' : 's'}.`
                      : 'Fills in on its own once medicines are added.'}
                </Form.Text>
              </Col>

              <Col xs={12} className="tests-block">
                <Form.Label className="tests-heading">
                  Tests advised
                  {/* <span className="text-muted fw-normal ms-2">
                    each one with the clinic it should be done at
                  </span> */}
                </Form.Label>
                <TestsAdvised
                  value={form.testsAdvised}
                  onChange={(v2) => set('testsAdvised', v2)}
                  clinics={referralClinics}
                  suggestions={COMMON_TESTS}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <div className="consult-actionbar d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div className="small">
            {dirty ? (
              <span className="text-warning-emphasis">
                <i className="bi bi-pencil-fill me-1" />
                Unsaved changes
              </span>
            ) : (
              <span className="text-success">
                <i className="bi bi-check2-circle me-1" />
                Everything saved
              </span>
            )}
            <span className="text-muted ms-3 d-none d-md-inline">
              {medicineCount} medicine{medicineCount === 1 ? '' : 's'}
              {form.diagnosis.length ? ` · ${form.diagnosis.join(', ')}` : ''}
            </span>
          </div>

          <div className="d-flex gap-2 flex-wrap">
            <Button variant="outline-secondary" onClick={() => save(false)} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-save me-2" />}
              Save Draft
            </Button>

            <Button size="lg" className="btn-clinic px-4" onClick={() => save(true)} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-printer me-2" />}
              Finish &amp; Print
            </Button>
          </div>
        </div>
    </>
  );
}
